/**
 * /ja/transcription — 文字起こし pillar reframe bridge (JA, single-locale).
 *
 * Targets 文字起こし (54k) / 音声文字起こし (15k) / 文字起こし無料 (9.9k). Honest
 * pivot per the multilingual plan: 文字起こしは「素材」、Draftlize は決定を生きた
 * プロダクトコンテキストに変える。No EN sibling — EN deliberately skips transcription.
 */
import { MarketingShell } from './MarketingShell';

export function JaTranscription() {
  return (
    <MarketingShell
      currentLocale="ja"
      hreflangPath="/transcription"
      hreflangLocales={['ja']}
      documentTitle="文字起こしの完全ガイド｜無料でテキスト化、その先へ · Draftlize"
      documentDescription="音声を無料で文字起こしする方法をまとめ、さらに大事な一歩——文字起こしから「意思決定」を取り出して活かす方法まで解説します。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">ガイド · 文字起こし</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            文字起こし——<br />
            <em>テキスト化は「ゴール」ではない。</em>
          </h1>
          <p className="lede">
            録音を貼るだけで、きれいな文字起こしが数秒で出てくる。Notta、PLAUD、Otter——文字起こしツールは「話したことを取りこぼさない」点で本当に優秀です。そのまま使い続けて構いません。でも、文字起こしは<em>素材</em>であって、成果物ではありません。その会議で本当に意味があったもの——下した<em>意思決定</em>とその理由——は、4,000字の文字の中に埋もれ、誰も読み返さないドキュメントに沈み、数週間で古くなります。このページは、文字起こしの「次の一歩」——会議の中の決定を、いつまでも最新であり続けるプロダクトコンテキストに変える話です。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">全部を文字にした。<em>それが問題なんです。</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.9, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            良い文字起こしは「網羅的」です。だからこそ、その会議が存在した理由である一つの決定が、雑談やランチの話と同じ重みで段落9に埋もれてしまう。いまや「記録する」ことはボトルネックではありません。ボトルネックは<em>抽出</em>です。会議に残る価値は小さい——いくつかの決定、その理由、そして次に動かす担当者。それ以外は、二度と読まないコンテキストです。
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.9, color: 'var(--ink-1)', margin: 0 }}>
            しかも、誰かが決定を取り出しても、それは議事録というフラットなテキストに着地し、影響するはずの仕様や、覆したはずの前の決定とは切り離されています。3週間後、プロダクトは前に進み、その決定は静かに古くなり、次の会議は「もう決めたはず」のことを蒸し返す——何が決まったのか、それがまだ有効なのか、誰も見つけられないからです。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">素材 vs 生きたコンテキスト</span>
            <h2 className="serif">文字起こしが終わる場所、<em>仕事が始まる場所。</em></h2>
          </div>
          <p>文字起こしツールと Draftlize は別の仕事をします。一方は会話を記録し、もう一方はその中の決定を生かし続ける。両方使えばいい。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>文字起こしツール</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>仕事</td><td>話したことを記録する</td><td className="ours yes">決めたことを最新に保つ</td></tr>
            <tr><td>出力</td><td className="partial">文字起こし＋要約</td><td className="ours yes">グラフ上の決定カード</td></tr>
            <tr><td>ある決定が前の決定を覆す</td><td className="no">無関係な2つのメモ</td><td className="ours yes">リンクされ、古い方が stale 表示</td></tr>
            <tr><td>3週間後</td><td className="no">ドキュメントに埋没</td><td className="ours yes">参照可能・最新か要確認か一目</td></tr>
            <tr><td>AI エージェントが使える</td><td className="partial">フラットな文字を読む</td><td className="ours yes">MCP 経由で読み書き</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">決定を<em>生きたコンテキスト</em>に変える。</h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            記録は文字起こしツールに任せる。肝心なのは、文字の中の数個の決定に何が起きるか——Draftlize では、それらは要約の一行であることをやめ、ずっと正しいままのグラフになります。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">決定は「行」ではなく「カード」になる</h3>
            <p>文字起こしや要約を貼ると、その中の決定が型付きのカードになります——決定そのもの、理由、検討した代替案、担当者。1時間の会話が、生き残らせるべき数個の要素に凝縮され、段落9に埋もれる代わりに ID で参照できます。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">新しい決定が、覆した古い決定を stale にする</h3>
            <p>今週の会議が先月の決定を変える。決定同士がリンクされているので、新しい決定は古い決定——とその上に立つ仕様——を自動で stale 表示にします。次の会議は決着済みのことを蒸し返しません。何が決まり、まだ有効かが、そこにあるからです。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI エージェントが「決まったこと」を読む</h3>
            <p>会議の決定は、Claude Code や Cursor が MCP 経由で読むコンテキストになります。次の仕様を書くエージェントは、火曜にチームが何を、なぜ決めたかを最初から知っている——毎回ゼロから文字起こしを読み直す必要はありません。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          文字起こしツールは会議を完璧に記録する。ただ「その中のどの決定が3週間後もまだ有効か」までは教えてくれない。
        </blockquote>
        <cite>文字起こしツールはそのまま。決定を生かし続けるのが Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">よくある質問。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '無料の文字起こしツールはどれがいい？', a: 'Notta・PLAUD・Otter など、用途に合うものを使ってください。Draftlize は録音や文字起こしはしません——文字起こしが出来上がった後、その中の決定を構造化したカードにして最新に保つのが役割です。' },
            { q: '文字起こしの精度はどのくらい？', a: 'ツールや音質・話者数によりますが、近年の AI 文字起こしは日本語でも実用レベルです。ただ精度が上がるほど「網羅的な文字の山」になり、肝心の決定が埋もれます。だからこそ抽出の一歩が重要です。' },
            { q: '長時間の音声でも文字起こしできる？', a: '多くのツールが長時間に対応します。問題は長さではなく、その後です。1時間の会議の価値は数個の決定に集約されます。Draftlize はそこだけをカードとして残します。' },
            { q: '文字起こしした後、どう活かせばいい？', a: '要約を Draftlize に貼り、中の決定をカードにします——決定・理由・担当者。全文を保存するのではなく、会議より長生きすべき数個の決定を参照可能で最新の状態に保つのが狙いです。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">文字起こしを、決定に変える<em>——$5 無料で。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新規登録で $5 付与</span>
          <span>使った分だけ支払い</span>
          <span>残高は無期限</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            文字起こしツールはそのまま。要約を Draftlize に貼れば、中の決定が生きたカードになります——リンクされ、最新で、AI エージェントも読める。次の会議が、決め直しではなく積み上げになります。
          </p>
          <a className="btn-primary" href="/">$5 無料で始める</a>
        </div>
      </section>
    </MarketingShell>
  );
}
